import CheckIcon from "@mui/icons-material/CheckRounded";
import RemoveIcon from "@mui/icons-material/CloseRounded";
import EditIcon from "@mui/icons-material/EditOutlined";
import RestoreIcon from "@mui/icons-material/ReplayRounded";
import type { SxProps, Theme } from "@mui/material";
import { Box, Button, Typography } from "@mui/material";
import type {
  GridColDef,
  GridColumnVisibilityModel,
  GridGroupingColDefOverride,
  GridPinnedColumnFields,
  GridRowGroupingModel,
  GridSortCellParams,
  GridSortModel,
} from "@mui/x-data-grid-premium";
import { GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD, useGridApiRef } from "@mui/x-data-grid-premium";
import { useMemo } from "react";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import DataGridGroupHeader from "@entrilia/shared/components/grid/DataGridGroupHeader";
import { useGridGroupsExpanded } from "@entrilia/shared/components/grid/gridHooks";
import StatusChip from "@entrilia/shared/components/StatusChip";
import UserWithAvatar from "@entrilia/shared/components/UserWithAvatar";
import { makeOpaqueTintedBackgroundFromColor } from "@entrilia/shared/utilities/colorHelper";
import type { FundInvestorContact } from "../../../../api/types/investorContactsTypes";
import type { AccessCategory } from "../../../../api/types/settingsTypes";
import PortalRoleChip from "../../../common/PortalRoleChip";
import useSettingsContext from "../../useSettingsContext";
import type {
  ContactChangesState,
  ContactFundUpdate,
  ContactRemovals,
  ContactRowUpdateHighlight,
} from "./contactChangesState";
import { getChangesCountChipLabel, getContactRemovalChipLabel } from "./contactChangeLabels";
import { isContactBeingRemoved, isContactFundRemoved } from "./contactChangesState";
import type { ContactSummary } from "./contactsListHelper";

const gridRowGroupingModel: GridRowGroupingModel = ["contactId"];

const gridColumnVisibilityModel: GridColumnVisibilityModel = {
  contactId: false,
  fundName: false,
};

const pinnedColumns: GridPinnedColumnFields = {
  left: [GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD, "roles"],
};

const pinnedColumnsWithActions: GridPinnedColumnFields = {
  ...pinnedColumns,
  right: ["actions"],
};

const gridSortModel: GridSortModel = [{ field: GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD, sort: "asc" }];

// Group children all belong to the same contact, so the first one represents the group
const getGroupFirstRow = ({ api, rowNode }: GridSortCellParams): FundInvestorContact | undefined => {
  if (rowNode.type !== "group") {
    return undefined;
  }

  const rowId = rowNode.children[0];
  return rowId ? (api.getRow(rowId) as FundInvestorContact | undefined) : undefined;
};

const getCurrentUserSortRank = (params: GridSortCellParams): number => {
  const row = getGroupFirstRow(params);
  return row ? (row.isCurrentUser ? -1 : 1) : 0;
};

const getGroupContactName = (params: GridSortCellParams): string => getGroupFirstRow(params)?.contactName ?? "";

// Sort by the current user first, then contact name (empty for leaves), then by cell value: contactId for groups (stable tie-breaker), fund name for leaves.
const contactAndFundNameSortComparator = (
  value1: string | undefined,
  value2: string | undefined,
  params1: GridSortCellParams<string | undefined>,
  params2: GridSortCellParams<string | undefined>
) =>
  getCurrentUserSortRank(params1) - getCurrentUserSortRank(params2) ||
  getGroupContactName(params1).localeCompare(getGroupContactName(params2)) ||
  (value1 ?? "").localeCompare(value2 ?? "");

const groupingColumnDefinition: GridGroupingColDefOverride<FundInvestorContact> = {
  headerName: "Name",
  leafField: "fundName",
  hideDescendantCount: true,
  minWidth: 400,
  flex: 3,
  cellClassName: "with-right-border",
  headerClassName: "with-right-border",
  renderHeader: (params) => <DataGridGroupHeader params={params} />,
  sortComparator: contactAndFundNameSortComparator,
};

const gridSx: SxProps<Theme> = (t) => ({
  ".MuiDataGrid-columnHeader.with-right-border, .MuiDataGrid-cell.with-right-border": {
    borderRight: `1px solid ${t.palette.divider}`,
  },
  ".MuiDataGrid-row--lastVisible .MuiDataGrid-cell": {
    borderBottom: `1px solid ${t.palette.divider}`,
  },
  ".MuiDataGrid-cell.pending-update": {
    backgroundColor: makeOpaqueTintedBackgroundFromColor(t, t.palette.warning.main, t.palette.background.paper),
  },
});

interface NameStatusChipProps {
  label: string;
  color: (t: Theme) => string;
}

const NameStatusChip = ({ label, color }: NameStatusChipProps) => (
  <Box component="span" sx={{ display: "inline-flex", ml: 1, verticalAlign: "middle" }}>
    <StatusChip label={label} color={color} />
  </Box>
);

const getActionsColumnDefinition = (
  contactsById: Map<string, ContactSummary>,
  addedContactIds: string[],
  removals: ContactRemovals,
  onRemoveContact: (contactId: string) => void,
  onRestoreContact: (contactId: string) => void,
  onEditContact: (row: FundInvestorContact) => void
): GridColDef<FundInvestorContact> => ({
  field: "actions",
  headerName: "",
  width: 140,
  align: "right",
  renderCell: ({ row, rowNode }) => {
    if (rowNode.type !== "group") {
      const isOwnRowWithoutAdminRole = row.isCurrentUser && row.role !== "Admin";
      if (addedContactIds.includes(row.contactId) || isOwnRowWithoutAdminRole) {
        return null;
      }

      return (
        <Button
          variant="text"
          color="secondary"
          startIcon={<EditIcon />}
          disabled={isContactFundRemoved(removals, row.contactId, row.fundId)}
          onClick={() => onEditContact(row)}
        >
          Edit
        </Button>
      );
    }

    const contactId = String(rowNode.groupingKey);
    const contact = contactsById.get(contactId);
    if (!contact || contact.isCurrentUser) {
      return null;
    }

    if (isContactBeingRemoved(removals, contactId)) {
      return (
        <Button
          variant="text"
          color="secondary"
          startIcon={<RestoreIcon />}
          onClick={() => onRestoreContact(contactId)}
        >
          Restore
        </Button>
      );
    }

    return (
      <Button variant="text" color="error" startIcon={<RemoveIcon />} onClick={() => onRemoveContact(contactId)}>
        Remove
      </Button>
    );
  },
});

const getColumnDefinitions = (
  categories: AccessCategory[],
  contactsById: Map<string, ContactSummary>,
  addedContactIds: string[],
  removals: ContactRemovals,
  contactUpdates: ContactFundUpdate[],
  updateHighlights: Map<string, ContactRowUpdateHighlight>,
  isEditable: boolean,
  onRemoveContact: (contactId: string) => void,
  onRestoreContact: (contactId: string) => void,
  onEditContact: (row: FundInvestorContact) => void
): GridColDef<FundInvestorContact>[] => [
  {
    field: "contactId",
    renderCell: ({ api, rowNode }) => {
      if (rowNode.type !== "group") {
        return null;
      }

      const contactId = String(rowNode.groupingKey);
      const contact = contactsById.get(contactId);
      if (!contact) {
        return null;
      }

      const updatesCount = contactUpdates.filter((update) => update.contactId === contactId).length;
      // Placeholder rows are affordances, not memberships, so they don't count toward the removal label
      const removalChipLabel = getContactRemovalChipLabel(
        removals,
        contactId,
        () =>
          rowNode.children.filter((rowId) => (api.getRow(rowId) as FundInvestorContact | undefined)?.isMember).length
      );

      return (
        <UserWithAvatar
          userName={contact.name}
          nameSuffix={
            <>
              {contact.isCurrentUser && (
                <Box component="span" sx={{ color: "primary.main" }}>
                  {" (You)"}
                </Box>
              )}
              {addedContactIds.includes(contactId) && (
                <NameStatusChip label="Adding" color={(t) => t.palette.info.main} />
              )}
              {removalChipLabel !== undefined && (
                <NameStatusChip label={removalChipLabel} color={(t) => t.palette.error.main} />
              )}
              {updatesCount > 0 && (
                <NameStatusChip label={getChangesCountChipLabel(updatesCount)} color={(t) => t.palette.warning.main} />
              )}
            </>
          }
          email={contact.email}
          userNameVariant="subtitle2"
        />
      );
    },
  },
  {
    field: "fundName",
    renderCell: ({ row, rowNode }) =>
      rowNode.type === "group" ? null : (
        <Typography
          noWrap
          color={isContactFundRemoved(removals, row.contactId, row.fundId) ? "textDisabled" : undefined}
          sx={{ pl: 11 }}
        >
          {row.fundName}
        </Typography>
      ),
  },
  {
    field: "roles",
    headerName: "Portal Role",
    minWidth: 120,
    flex: 1,
    align: "center",
    headerAlign: "center",
    cellClassName: ({ row, rowNode }) =>
      rowNode.type !== "group" && updateHighlights.get(row.id)?.roleChanged ? "pending-update" : "",
    renderCell: ({ row, rowNode }) => (rowNode.type === "group" ? null : <PortalRoleChip role={row.role ?? "None"} />),
  },
  ...categories.map<GridColDef<FundInvestorContact>>((category) => ({
    field: category.id,
    headerName: category.name,
    minWidth: 160,
    flex: 1,
    align: "center",
    headerAlign: "center",
    cellClassName: ({ row, rowNode }) =>
      rowNode.type !== "group" && updateHighlights.get(row.id)?.changedCategoryExternalIds.includes(category.externalId)
        ? "pending-update"
        : "",
    renderCell: ({ row, rowNode }) =>
      rowNode.type === "group" || !row.categoryExternalIds.includes(category.externalId) ? null : (
        <CheckIcon color="success" />
      ),
  })),
  ...(isEditable
    ? [
        getActionsColumnDefinition(
          contactsById,
          addedContactIds,
          removals,
          onRemoveContact,
          onRestoreContact,
          onEditContact
        ),
      ]
    : []),
];

interface Props {
  rows: FundInvestorContact[];
  contactsById: Map<string, ContactSummary>;
  contactChanges: ContactChangesState;
  updateHighlights: Map<string, ContactRowUpdateHighlight>;
  isEditable: boolean;
  onRemoveContact: (contactId: string) => void;
  onRestoreContact: (contactId: string) => void;
  onEditContact: (row: FundInvestorContact) => void;
}

const ContactsGrid = ({
  rows,
  contactsById,
  contactChanges,
  updateHighlights,
  isEditable,
  onRemoveContact,
  onRestoreContact,
  onEditContact,
}: Props) => {
  const { accessCategories } = useSettingsContext();
  const apiRef = useGridApiRef();
  const { isGroupExpanded } = useGridGroupsExpanded(apiRef);

  // Destructured so the columns stay referentially stable when unrelated state (e.g. the comment) changes
  const { newContacts, removedContactIds, removedContactFunds, contactUpdates } = contactChanges;

  const addedContactIds = useMemo(() => newContacts.map((contact) => contact.contactId), [newContacts]);
  const removals = useMemo<ContactRemovals>(
    () => ({ removedContactIds, removedContactFunds }),
    [removedContactIds, removedContactFunds]
  );

  const columns = useMemo(
    () =>
      getColumnDefinitions(
        accessCategories,
        contactsById,
        addedContactIds,
        removals,
        contactUpdates,
        updateHighlights,
        isEditable,
        onRemoveContact,
        onRestoreContact,
        onEditContact
      ),
    [
      accessCategories,
      contactsById,
      addedContactIds,
      removals,
      contactUpdates,
      updateHighlights,
      isEditable,
      onRemoveContact,
      onRestoreContact,
      onEditContact,
    ]
  );

  return (
    <DataGrid<FundInvestorContact>
      apiRef={apiRef}
      rows={rows}
      columns={columns}
      groupingColDef={groupingColumnDefinition}
      rowGroupingModel={gridRowGroupingModel}
      columnVisibilityModel={gridColumnVisibilityModel}
      pinnedColumns={isEditable ? pinnedColumnsWithActions : pinnedColumns}
      isGroupExpandedByDefault={isGroupExpanded}
      sortModel={gridSortModel}
      multilineRows
      noRowsText="No contacts"
      disableColumnSorting
      disableColumnReorder
      sx={gridSx}
    />
  );
};

export default ContactsGrid;
